import * as React from 'react';
import { LocalDateTime } from 'js-joda';

import { DeliveryAddress } from 'state/delivery';
import { IconMapMarker } from '@tock/icons';
import { ReservationMetadataList } from 'components/business/reservation/ReservationMetadataList';
import { Typography } from 'omakase';
import { consumer, core } from '@tock/coretypes';
import { isIOSNative } from 'utils/deviceUtils';
import { offeringConsumerDetailPageRelativeLink } from '@tock/shared/utils/offerings';
import { useExplorePath } from 'hooks/useExplorePath';
import Anchor from 'components/Anchor';
import IconBlank from 'components/icons/svgr/IconBlank';

// TODO(jacob, 2019-11-13): Some of these fields are redundant. We should take
// ticket type and get rid of ticketTypeName and maybe isEvent (though it looks
// like TicketTypeVariety is an admin-only field for some reason?)
interface PurchaseDetailsProps {
  business: consumer.BusinessConfig | core.IBusinessPublic;
  children?: React.ReactNode;
  confirmationId?: string;
  dateTime: LocalDateTime;
  deliveryAddress?: DeliveryAddress;
  diner?: core.Patron;
  isAuthenticated?: boolean;
  isEvent: boolean;
  onSwitchAccounts?(): void;
  owner?: core.Patron;
  partySize: number;
  purchase?: core.PastPurchase;
  seatingAreaName?: string;
  status?: string;
  mobileTicketUrl?: string;
  ticketTypeName: string;
  ticketType: core.TicketType;
  inVisitEdit?: boolean;
}

export const PurchaseDetails: React.FC<PurchaseDetailsProps> = ({
  business,
  children,
  confirmationId,
  dateTime,
  deliveryAddress,
  diner,
  isAuthenticated,
  isEvent,
  onSwitchAccounts,
  owner,
  partySize,
  status,
  ticketTypeName,
  ticketType,
  inVisitEdit,
  seatingAreaName,
}) => {
  const consumerUrl = `/${business.domainName}`;
  const experienceUrl =
    isEvent && ticketType && `${consumerUrl}/${offeringConsumerDetailPageRelativeLink(ticketType)}`;
  const isDelivery = ticketType.variety === 'ORDER_DELIVERY';
  const reserveShippingTime = ticketType.reserveShippingTime;

  const isWidget = window !== window.parent;
  const explorePath = useExplorePath();

  if (inVisitEdit) {
    return (
      <div className="Receipt-content Receipt-container--border ConsumerCheckout-section">
        <div className="Receipt-container--body">{children}</div>
      </div>
    );
  }

  const handleBusinessNameClick = () => {
    if (isWidget) {
      window.parent.postMessage(
        {
          source: 'tock',
          action: 'redirect',
          redirectTo: `https://${explorePath}${consumerUrl}`,
        },
        '*'
      );
    }
  };

  return (
    <div className="Receipt-content Receipt-container--border ConsumerCheckout-section">
      <div className="Receipt-container--header">
        <div className="Receipt-container--businessAndConfirmationContainer">
          {isIOSNative() ? (
            <Typography color="inherit" component="span" variant="heading04">
              {business.name}
            </Typography>
          ) : (
            <Anchor
              className="Receipt-content--nameLink"
              href={consumerUrl}
              onClick={handleBusinessNameClick}
            >
              <Typography
                color="inherit"
                component="span"
                variant="heading04"
                data-testid="confirmation-business-name"
              >
                {business.name}
              </Typography>
            </Anchor>
          )}

          {confirmationId && (
            <Typography
              variant="body02"
              color="fgSubtle"
              component="p"
            >{`Confirmation #: ${confirmationId}`}</Typography>
          )}
        </div>
        <div data-testid="experience-info">
          <Typography color="inherit" component="span" variant="body02">
            {experienceUrl ? (
              <Anchor className="Receipt-content--nameLink" href={experienceUrl}>
                {ticketTypeName}
              </Anchor>
            ) : (
              ticketTypeName
            )}
          </Typography>
        </div>
      </div>

      {status && (
        <p className="Receipt-statusBanner" data-testid="receipt-label">
          <Typography color="inherit" component="span" variant="body02">
            <span>{status}</span>
          </Typography>
        </p>
      )}

      <div className="Receipt-container--body">
        <ReservationMetadataList
          showDateAndTime
          showQuantity
          showDinerAndOwner={isAuthenticated ?? false}
          showSeatingArea
          isShipping={reserveShippingTime}
          ticketTypeVariety={ticketType.variety}
          ticketTypeDescriptiveVariety={ticketType.descriptiveVariety}
          singleUnitQuantity={ticketType.singleUnitQuantity}
          deliveryServiceProvider={ticketType.deliveryServiceProvider}
          dateTime={dateTime}
          purchaseCount={partySize}
          purchaseOwner={owner}
          purchaseDiner={diner}
          inVisitEdit={inVisitEdit}
          onSwitchAccounts={onSwitchAccounts}
          seatingAreaName={seatingAreaName}
        />

        {/* Check both in case we have a delivery address saved from an earlier purchase attempt */}
        {isDelivery && deliveryAddress && (
          <div className="Receipt-container--section is-inline">
            <span className="Receipt-reservationMeta">
              <IconMapMarker className="Consumer-reservationMetaItemIconNew" />
              <div className="Receipt-reservationMetaContent" data-testid="delivery-address">
                <Typography component="span" variant="body02Strong">
                  Delivery address
                </Typography>
              </div>
            </span>
            <span className="Receipt-reservationMeta">
              <IconBlank className="Consumer-reservationMetaItemIconNew" />
              <div className="Receipt-reservationMetaContent" data-testid="delivery-address-line-1">
                <Typography color="inherit" component="span" variant="body02">
                  {deliveryAddress.address.address}
                </Typography>
              </div>
            </span>
            {deliveryAddress.address.address2 && (
              <span className="Receipt-reservationMeta">
                <IconBlank className="Consumer-reservationMetaItemIconNew" />
                <div
                  className="Receipt-reservationMetaContent"
                  data-testid="delivery-address-line-2"
                >
                  <Typography color="inherit" component="span" variant="body02">
                    {deliveryAddress.address.address2}
                  </Typography>
                </div>
              </span>
            )}
            <span className="Receipt-reservationMeta">
              <IconBlank className="Consumer-reservationMetaItemIconNew" />
              <div className="Receipt-reservationMetaContent" data-testid="delivery-address-line-3">
                <Typography color="inherit" component="span" variant="body02">
                  {deliveryAddress.address.city}, {deliveryAddress.address.state}{' '}
                  {deliveryAddress.address.zipCode}
                </Typography>
              </div>
            </span>
          </div>
        )}

        {children}
      </div>
    </div>
  );
};
